import _ from 'lodash';
import DDLogs from '@/DDLogs';
import * as moment from 'moment';
import onboardingApi from '../../api/OnbordingApi';
import consts from '../../constants/onboarding-state';
import CredentialsApi from '../../api/CredentialsApi';

const state = {
  isLoading: false,
  pageName: null,
  error: null,
  pageParams: null,
  credentials: [],
  saveCredentialsError: false,
  nameAlreadyUsedError: false,
  loadingStartTime: null,
  asyncTimeoutReached: false,
  timeout: null,
};

const TIMEOUT_IN_SECS = 60;

const getters = {
  invalidCredentials: state => state.error === 'invalidCredentials',
  sourceName: state => (state.pageParams ? state.pageParams.sourceName : null),
  onboardingCompleted: state => state.pageName && state.pageName === consts.ONBOARDING_PAGE.CASHFLOW,
};

const actions = {
  async handleOnboardingState({ dispatch }) {
    if (state.isLoading) {
      dispatch('handleLoadingState');
    }
  },

  async getOnboardingState() {
    const { onboardingState } = await onboardingApi.fetchOnboardingState();
    return onboardingState;
  },

  async setOnboardingState({ commit, dispatch, state }, onboardingState) {
    commit('setPageName', onboardingState.onboardingPage);
    commit('setPageParams', onboardingState.pageParams);
    if (onboardingState.pageParams) {
      commit('setLoadingState', !!onboardingState.pageParams.loading);
      commit('setError', onboardingState.pageParams.error);
      commit('setLoadingStartTime', onboardingState.pageParams.loadingStartTime);
    } else {
      commit('setLoadingState', false);
      commit('setError', null);
      commit('setLoadingStartTime', null);
    }
    if (finishedOnboardingFlow(state.pageName)) {
      commit('setPageName', consts.ONBOARDING_PAGE.CASHFLOW_LOADER);
    }
    if (!state.isLoading) {
      commit('setAsyncTimeoutReached', false);
      dispatch('clearTimeout');
    }
  },

  async fetchOnboardingState({ dispatch }) {
    DDLogs.log('fetchOnboardingState', onboardingState);
    const onboardingState = await dispatch('getOnboardingState');
    if (onboardingState) {
      dispatch('setOnboardingState', onboardingState);
    }
  },

  async fetchOnboardingStateIfFinishedLoading({ commit, dispatch }) {
    const onboardingState = await dispatch('getOnboardingState');
    DDLogs.log('fetchOnboardingStateIfFinishedLoading', onboardingState);
    if (onboardingState && !onboardingState.isLoading) {
      dispatch('setOnboardingState', onboardingState);
    }
  },
  handleLoadingState({ commit, state, dispatch }) {
    const loadingTimeInSecs = moment().diff(moment(state.loadingStartTime), 'seconds');
    if (loadingTimeInSecs > TIMEOUT_IN_SECS) {
      commit('setAsyncTimeoutReached', true);
    } else {
      dispatch('createTimeout', loadingTimeInSecs);
    }
  },
  createTimeout({ commit, state, dispatch }, loadingTimeInSecs) {
    dispatch('clearTimeout');
    const timeout = setTimeout(async () => {
      await dispatch('fetchOnboardingStateIfFinishedLoading');
      if (state.isLoading) {
        commit('setAsyncTimeoutReached', true);
      }
    }, (TIMEOUT_IN_SECS - loadingTimeInSecs) * 1000);
    commit('setTimeout', timeout);
  },
  async handleBack({ commit, dispatch }, pageParams) {
    await onboardingApi.back();
    await dispatch('fetchOnboardingState');
    if (pageParams) {
      commit('setPageParams', pageParams);
    }
  },
  deletePageParams({ commit, dispatch }, pageParams) {
    if (pageParams) {
      commit('setPageParams', pageParams);
    } else {
      commit('setPageParams', null);
    }
    dispatch('handleBack', pageParams);
  },
  async deletePendingCreds({ commit, dispatch }, pageParams) {
    commit('setLoadingState', true);
    await CredentialsApi.deletePendingCredentials();
    await dispatch('fetchOnboardingState');
  },
  async nextPage({ commit, dispatch }) {
    const { onboardingState } = await onboardingApi.nextOnboardingPage();
    dispatch('setOnboardingState', onboardingState);
  },
  async botiIntroSubmit({ dispatch }) {
    const { onboardingState } = await onboardingApi.botiIntroSubmit();
    dispatch('setOnboardingState', onboardingState);
  },
  async progressIndicationOneSubmit({ dispatch }) {
    const { onboardingState } = await onboardingApi.progressIndicationOneSubmit();
    dispatch('setOnboardingState', onboardingState);
  },
  async progressIndicationTwoSubmit({ dispatch }) {
    const { onboardingState } = await onboardingApi.progressIndicationTwoSubmit();
    dispatch('setOnboardingState', onboardingState);
  },
  async securityInformationSubmit({ dispatch }) {
    const { onboardingState } = await onboardingApi.securityInformationSubmit();
    dispatch('setOnboardingState', onboardingState);
  },
  async accountInvestigationSubmit({ commit, dispatch }, inputs) {
    commit('setLoadingState', true);
    const { onboardingState } = await onboardingApi.accountInvestigationSubmit(inputs);
    dispatch('setOnboardingState', onboardingState);
  },
  async incomeInvestigationSubmit({ commit, dispatch }, inputs) {
    commit('setLoadingState', true);
    const { onboardingState } = await onboardingApi.incomeInvestigationSubmit(inputs);
    dispatch('setOnboardingState', onboardingState);
  },
  async expenseInvestigationSubmit({ commit, dispatch }, inputs) {
    commit('setLoadingState', true);
    const { onboardingState } = await onboardingApi.expenseInvestigationSubmit(inputs);
    dispatch('setOnboardingState', onboardingState);
  },
  async savePageName({ commit, dispatch }, pageName) {
    commit('setPageName', pageName);
    await onboardingApi.updateOnboardingPage(pageName);
  },
  clearTimeout({ state }) {
    if (state.timeout) {
      clearTimeout(state.timeout);
    }
  },
  async saveCredentials({ commit, dispatch, state }, { sourceName, credentials, name, sourceType }) {
    commit('setSaveCredentialsError', false);
    if (!state.error) {
      await dispatch('validateName', { sourceName, name });
      if (state.nameAlreadyUsedError) {
        return;
      }
    }
    try {
      commit('setLoadingStartTime', moment().toString());
      commit('setLoadingState', true);
      await CredentialsApi.saveCredentials({ sourceName, credentials, name, sourceType });
      dispatch('createTimeout', 0);
    } catch (e) {
      commit('setSaveCredentialsError', true);
      commit('setLoadingState', false);
    }
  },
  async savePendingCredentials({ commit, dispatch, state }, { sourceName, credentials, name }) {
    commit('setSaveCredentialsError', false);
    try {
      commit('setLoadingState', true);
      return await CredentialsApi.savePendingCredentials({ sourceName, credentials, name });
    } catch (e) {
      commit('setSaveCredentialsError', true);
      commit('setLoadingState', false);
      return null;
    }
  },
  async validateName({ commit, dispatch, state }, { sourceName, name }) {
    await dispatch('fetchCredentials');
    const existingCreds = _.find(state.credentials, { sourceName, name });
    if (existingCreds && existingCreds.status !== 'pending') {
      commit('setNameAlreadyInUseError', true);
    }
  },
  async fetchCredentials({ commit }) {
    const credentials = await CredentialsApi.fetchCredentials();
    commit('setCredentials', credentials);
  },
};

function finishedOnboardingFlow(pageName) {
  return pageName === consts.ONBOARDING_PAGE.CASHFLOW;
}

const mutations = {
  setPageName(state, pageName) {
    state.pageName = pageName;
  },
  setPageParams(state, pageParams) {
    state.pageParams = pageParams;
  },
  setLoadingState(state, isLoading) {
    DDLogs.log(`setLoadingState to ${isLoading}`);
    state.isLoading = isLoading;
  },
  setLoadingStartTime(state, loadingStartTime) {
    state.loadingStartTime = loadingStartTime;
  },
  setError(state, error) {
    state.error = error;
  },
  setNameAlreadyInUseError(state, nameAlreadyUsedError) {
    state.nameAlreadyUsedError = nameAlreadyUsedError;
  },
  setSaveCredentialsError(state, saveCredentialsError) {
    state.saveCredentialsError = saveCredentialsError;
  },
  setCredentials(state, credentials) {
    state.credentials = credentials;
  },
  setAsyncTimeoutReached(state, asyncTimeoutReached) {
    DDLogs.log('setAsyncTimeoutReached', asyncTimeoutReached);
    state.asyncTimeoutReached = asyncTimeoutReached;
  },
  setTimeout(state, timeout) {
    state.timeout = timeout;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
